import axios from "axios";
import { BulkContractEnrollmentSummaryRecord } from "models/view-models/enrollments/bulk-contract-enrollment-summary-record";
import { CreateBulkEnrollmentDto } from "models/interfaces/enrollments/create-bulk-enrollment-dto";
import { CreateEnrollmentDto } from "models/interfaces/enrollments/create-enrollment-dto";
import { EncodingType } from "models/enumerations/encoding/encoding-type";
import { EnrollmentRecord } from "models/view-models/enrollments/enrollment-record";
import { ServiceResponse, ServiceUtils } from "andculturecode-javascript-core";
import { useCallback } from "react";
import { useCancellablePromise } from "andculturecode-javascript-react";

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const baseEndpoint: string = "ollContracts/:contractId/enrollments";
const resourceType: typeof EnrollmentRecord = EnrollmentRecord;
const bulkCreateEndpoint: string = `${baseEndpoint}/:encodingType/bulkUpload`;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Types
// -------------------------------------------------------------------------------------------------

type CreateOllContractEnrollmentService = (
    contractId: number,
    params: CreateEnrollmentDto
) => Promise<ServiceResponse<EnrollmentRecord>>;

type CreateBulkOllContractEnrollmentService = (
    contractId: number,
    encodingType: EncodingType,
    params: CreateBulkEnrollmentDto
) => Promise<ServiceResponse<BulkContractEnrollmentSummaryRecord>>;

// #endregion Types

// -------------------------------------------------------------------------------------------------
// #region Service
// -------------------------------------------------------------------------------------------------

const OllContractEnrollmentService = {
    /**
     * Custom hook for leveraging service create calls in React components
     */
    useCreate: (): { create: CreateOllContractEnrollmentService } => {
        const { cancellablePromise } = useCancellablePromise();

        const serviceCreate: CreateOllContractEnrollmentService = (
            contractId: number,
            params: CreateEnrollmentDto
        ): Promise<ServiceResponse<EnrollmentRecord>> => {
            const endPointWithPathIdsReplaced = baseEndpoint.replace(
                ":contractId",
                contractId.toString()
            );

            return axios
                .post(endPointWithPathIdsReplaced, params)
                .then((r) => ServiceUtils.mapAxiosResponse(resourceType, r));
        };

        function create(
            contractId: number,
            params: CreateEnrollmentDto
        ): Promise<ServiceResponse<EnrollmentRecord>> {
            return cancellablePromise(serviceCreate(contractId, params)) as Promise<
                ServiceResponse<EnrollmentRecord>
            >;
        }

        return { create: useCallback(create, [cancellablePromise]) };
    },

    /**
     * Custom hook for leveraging service create calls in React components
     */
    useBulkCreate: (): { create: CreateBulkOllContractEnrollmentService } => {
        const { cancellablePromise } = useCancellablePromise();

        const serviceCreate: CreateBulkOllContractEnrollmentService = (
            contractId: number,
            encodingType: EncodingType,
            params: CreateBulkEnrollmentDto
        ): Promise<ServiceResponse<BulkContractEnrollmentSummaryRecord>> => {
            var endPointWithPathIdsReplaced = bulkCreateEndpoint.replace(
                ":contractId",
                contractId.toString()
            );

            endPointWithPathIdsReplaced = endPointWithPathIdsReplaced.replace(
                ":encodingType",
                encodingType.toString()
            );

            return axios
                .post(endPointWithPathIdsReplaced, params)
                .then((r) => ServiceUtils.mapAxiosResponse(BulkContractEnrollmentSummaryRecord, r));
        };

        function create(
            contractId: number,
            encodingType: EncodingType,
            params: CreateBulkEnrollmentDto
        ): Promise<ServiceResponse<BulkContractEnrollmentSummaryRecord>> {
            return cancellablePromise(serviceCreate(contractId, encodingType, params)) as Promise<
                ServiceResponse<BulkContractEnrollmentSummaryRecord>
            >;
        }

        return { create: useCallback(create, [cancellablePromise]) };
    },
};

// #endregion Service

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { OllContractEnrollmentService };

// #endregion Exports
